import React, { useEffect } from 'react';

import { Container } from '@material-ui/core';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { Redirect, useParams } from 'react-router-dom';

import Loader from 'components/Loader';
import ManagePageHeader from 'components/ManagePageHeader/ManagePageHeader';
import { actionTemplatesApi } from 'config/api/actionLibrary/actionLibrary';
import { parseActionTemplateForForm } from 'config/api/actionLibrary/actionLibrary.parsers';
import action_library_messages from 'messages/action_library_messages';
import useCanEditActionTemplate from 'pages/ActionLibrary/hooks/useCanEditActionTemplate';
import PATHS from 'router/PATHS';

import ActionTemplateForm from '../ActionLibraryCreatePage/ActionTemplateForm';
import useActionTemplateFormManager from '../ActionLibraryCreatePage/useActionTemplateFormManager';

const ActionLibraryEditPage: React.FC = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { id } = useParams<{ id: string }>();
  const canEdit = useCanEditActionTemplate();

  const { data, isLoading } = useQuery(['actionTemplate', id], () => actionTemplatesApi.getSingleActionTemplate(Number(id)));

  const { allowNextNavigation, setIsDirty, saveHandlerRef, handleNavigation, NavigationPrompt } = useActionTemplateFormManager({
    isEditMode: true,
    templateId: Number(id),
  });

  useEffect(() => {
    if (data && !canEdit(data.data)) {
      enqueueSnackbar(t(action_library_messages.errors.edit_permission_denied), { variant: 'error' });
    }
  }, [data, canEdit, enqueueSnackbar, t]);

  if (isLoading || !data) {
    return <Loader inner />;
  }

  if (!canEdit(data.data)) {
    return <Redirect to={PATHS.ACTION_LIBRARY_DETAIL.replace(':id', id)} />;
  }

  const initialValues = parseActionTemplateForForm(data.data);

  return (
    <Container>
      <NavigationPrompt />
      <ManagePageHeader backButton onBack={handleNavigation} subtitle={t(action_library_messages.edit_subtitle)}>
        {t(action_library_messages.actions.edit)}
      </ManagePageHeader>
      <ActionTemplateForm
        initialValues={initialValues}
        isEditMode
        onAllowNavigationBeforePush={allowNextNavigation}
        onCancelNavigation={handleNavigation}
        onDirtyStateChange={setIsDirty}
        onSaveRequestRef={saveHandlerRef}
        templateId={Number(id)}
      />
    </Container>
  );
};

export default ActionLibraryEditPage;
